import React, { useState } from "react";
import { useSession } from "@supabase/auth-helpers-react";
import { RecordingWidgetState, useAssetLibraryProvider } from "@/state/providers/AssetLibraryProvider";
import WidgetMicBar from "@/features/recording-widget/WidgetMicBar";
import { useMic } from "@/features/recording-widget/mic/MicProvider";
import { MIC_BLOCKING_STATES } from "@/features/recording-widget/mic/micStatus";
import { useAccountContext } from "@/state/providers/AccountProvider";
import MicrophoneIcon from "@icons/microphone-01.svg?react";
import PhoneCallIcon from "@icons/phone-call-01.svg?react";
import MinimizeIcon from "@icons/minimize-01.svg?react";
import classNames from "classnames";
import { useTour } from "@providers/TourProvider";
import {
    QuickStartMode,
    readQuickStartMode,
    writeQuickStartMode,
} from "@/features/recording-widget/quickStartModePersistence";

type WaitingViewProps = {
    onMinimize: () => void;
    isNarrowViewport?: boolean;
};

// view for when widget is in the "waiting" state (before recording)
const WaitingView: React.FC<WaitingViewProps> = ({ onMinimize, isNarrowViewport = false }) => {
    /**
     * Audio Recording Provider
     * hook for managing widget state, audio recording, file upload, document select, patient data
     **/
    const {
        // audio recording controls
        recorderControls: { recordingState, isRecording, isExtensionTransport },
        // recording widget controls
        recordingWidgetControls: { setRecordingWidgetState, handleStartRecording },
        // phone call widget controls
        phoneCallWidgetControls: { renderPhoneCallWidget, handleStartQuickStartCall, isStartingQuickStartCall },
    } = useAssetLibraryProvider();

    const mic = useMic();
    // The record-press gate lives in AssetLibraryProvider; this only mirrors it on the controls.
    // The extension records without opening a mic here, so it is never blocked.
    const micBlocked = !isExtensionTransport && MIC_BLOCKING_STATES.has(mic.state);

    const { accountData } = useAccountContext();
    const phoneCallsEnabled = Boolean(accountData?.phone_calls_enabled);

    const session = useSession();
    const userId = session?.user?.id;

    const { activeTour } = useTour();
    const [patientName, setPatientName] = useState<string>("");
    const [mode, setMode] = useState<QuickStartMode>(() => (userId ? readQuickStartMode(userId) : "record"));
    const effectiveMode = phoneCallsEnabled ? mode : "record";

    // Bidirectional guard: recording is blocked while a call is active, and calling
    // is blocked while a recording is active — same pattern as RecordingModeButton.
    const recordDisabled = renderPhoneCallWidget || isStartingQuickStartCall || micBlocked;
    const callDisabled = isRecording || renderPhoneCallWidget || isStartingQuickStartCall;
    const recordDisabledTitle =
        micBlocked && !(renderPhoneCallWidget || isStartingQuickStartCall) ? undefined : "Finish call to record";
    const callDisabledTitle = isRecording ? "Finish recording to call" : "Finish current call to start another";

    const selectMode = (nextMode: QuickStartMode) => {
        setMode(nextMode);
        if (userId) writeQuickStartMode(userId, nextMode);
    };

    const circleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (effectiveMode === "call") {
            const success = await handleStartQuickStartCall(patientName || undefined);
            if (success) setPatientName("");
        } else {
            mic.closeList();
            setRecordingWidgetState(RecordingWidgetState.RECORDING);
            handleStartRecording({ patientName });
        }
    };

    const circleDisabled = effectiveMode === "call" ? callDisabled : recordDisabled;
    const circleTitle =
        effectiveMode === "call"
            ? callDisabled
                ? callDisabledTitle
                : undefined
            : recordDisabled
              ? recordDisabledTitle
              : undefined;

    return (
        <div
            className={classNames(
                "w-full bg-shadow-900 rounded-lg shadow flex flex-col gap-2",
                isNarrowViewport ? "pt-2" : "pt-3",
                // the mic bar is the last row; the extension shows only recordingMessage rows there, so keep the old bottom padding
                isExtensionTransport && (isNarrowViewport ? "pb-2" : "pb-3"),
            )}
        >
            <div className="flex items-center gap-2 sm:gap-4 px-2 sm:px-4">
                <button
                    data-tour="record-button"
                    aria-label={effectiveMode === "call" ? "Start call" : "Start recording"}
                    className={classNames(
                        "shrink-0 flex justify-center items-center rounded-full border-shadow-800 text-white",
                        isNarrowViewport ? "w-16 h-16 border-4" : "w-[105px] h-[105px] border-8",
                        circleDisabled ? "bg-max-200" : "bg-max-900 hover:bg-max-800",
                    )}
                    disabled={circleDisabled}
                    title={circleTitle}
                    onClick={circleClick}
                >
                    {effectiveMode === "call" ? (
                        <PhoneCallIcon className={isNarrowViewport ? "w-6 h-6 text-white" : "w-12 h-12 text-white"} />
                    ) : (
                        <MicrophoneIcon className={isNarrowViewport ? "w-6 h-6 text-white" : "w-12 h-12 text-white"} />
                    )}
                </button>

                {/* right column */}
                <div
                    className={classNames(
                        "flex flex-col ml-0 sm:ml-1 mr-4 w-full",
                        isNarrowViewport ? "gap-1" : "gap-2",
                    )}
                >
                    <div className="flex items-center justify-between">
                        <div className="text-white text-xs sm:text-sm font-medium font-jakarta leading-tight">
                            Quick Start
                        </div>
                        {!activeTour && (
                            <button
                                type="button"
                                aria-label="Minimize Quick Start"
                                className="text-white/70 hover:text-white"
                                onClick={onMinimize}
                            >
                                <MinimizeIcon className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {/* patient name input — placeholder is dropped on narrow viewports to save
                        room; aria-label keeps the field accessible either way. */}
                    <input
                        id="recording-widget-patient-name"
                        type="text"
                        name="patient-name"
                        aria-label="Patient name (optional)"
                        placeholder={isNarrowViewport ? undefined : "Patient name (optional)"}
                        value={patientName}
                        onChange={(e) => {
                            setPatientName(e.target.value);
                        }}
                        disabled={recordingState === "recording" || recordingState === "paused"}
                        className="block w-full rounded-md border-0 py-1.5 px-3 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focused-inset text-xs leading-tight sm:text-sm sm:leading-6 text-gray-900"
                    />

                    {phoneCallsEnabled && (
                        <div className="flex gap-2">
                            <button
                                type="button"
                                aria-label="Call"
                                onClick={() => selectMode("call")}
                                disabled={callDisabled}
                                title={callDisabled ? callDisabledTitle : undefined}
                                className={classNames(
                                    "flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium",
                                    mode === "call"
                                        ? "bg-max-900 text-white"
                                        : "bg-shadow-800 text-white/70 hover:bg-shadow-700",
                                    callDisabled && "opacity-50 cursor-not-allowed",
                                )}
                            >
                                <PhoneCallIcon className="w-3.5 h-3.5" />
                                {!isNarrowViewport && "Call"}
                            </button>
                            <button
                                type="button"
                                aria-label="Record"
                                onClick={() => selectMode("record")}
                                disabled={recordDisabled}
                                title={recordDisabled ? recordDisabledTitle : undefined}
                                className={classNames(
                                    "flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium",
                                    mode === "record"
                                        ? "bg-max-900 text-white"
                                        : "bg-shadow-800 text-white/70 hover:bg-shadow-700",
                                    recordDisabled && "opacity-50 cursor-not-allowed",
                                )}
                            >
                                <MicrophoneIcon className="w-3.5 h-3.5" />
                                {!isNarrowViewport && "Record"}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <WidgetMicBar />
        </div>
    );
};

export default WaitingView;
